import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeWhile, concatMap } from 'rxjs';
import { MweConstants } from 'src/app/constants/mwe-constants.service';
import { MasterDataService } from 'src/app/services/master-data.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-career-filter',
  templateUrl: './career-filter.component.html',
  styleUrls: ['./career-filter.component.scss']
})
export class CareerFilterComponent implements OnInit {
  countryDropdown: any[] = [];
  states: any[] = [];
  cities: any[] = [];
  jobCategories: any[] = [];
  jobSkills: any[] = [];
  statusOptions: string[] = ['Active', 'Inactive'];
  filteredStatusOptions: string[] = this.statusOptions;
  chips: { name: string, type: string }[] = [];

  careerFilterForm: FormGroup = new FormGroup({
    postedDate: new FormControl(null),
    jobCategories: new FormControl(''),
    skills: new FormControl(''),
    experience: new FormControl(null),
    company: new FormControl(null),
    countries: new FormControl(''),
    cities: new FormControl(''),
    states: new FormControl(''),
    showOnlyActiveJobPosts: new FormControl(false),
    status: new FormControl(null)
  });

  @Output() clearCareersFilter: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('postedDate') postedDatePicker!: MatDatepicker<Date>;
  @ViewChild('autoCompleteCountryInput', { read: MatAutocompleteTrigger }) autoCompleteCountryInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteStateInput', { read: MatAutocompleteTrigger }) autoCompleteStateInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteCityInput', { read: MatAutocompleteTrigger }) autoCompleteCityInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteStatusInput', { read: MatAutocompleteTrigger }) autoCompleteStatusInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteJobCategoryInput', { read: MatAutocompleteTrigger }) autoCompleteJobCategoryInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteSkillInput', { read: MatAutocompleteTrigger }) autoCompleteSkillInput!: MatAutocompleteTrigger;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<any>,
    private masterDataService: MasterDataService
  ) {}

  ngOnInit(): void {
    this.getMasterData();
    this.initializeExistingFilters();
  }

  private initializeExistingFilters() {
    if (this.data) {
      const existingFilter = Array.isArray(this.data) ? this.data[0] : this.data;
      if (existingFilter) {
        const arrayFields = ['jobCategories', 'skills', 'countries', 'states', 'cities'];
        const singleValueFields = ['postedDate', 'experience', 'company', 'status'];
        Object.keys(existingFilter).forEach(key => {
          const value = existingFilter[key];

          if (arrayFields.includes(key)) {
            this.checkAndConvertArray(value).forEach((item: string) => {
              this.chips.push({ name: item, type: key });
            });
            this.careerFilterForm.get(key)?.setValue('');
          } else if (singleValueFields.includes(key)) {
            if (value) {
              if (key === 'postedDate') {
                this.chips.push({ name: this.formatDate(value), type: key });
              } else if (key === 'experience') {
                this.chips.push({ name: `${value} years`, type: key });
              } else if (key === 'company') {
                this.chips.push({ name: value, type: key });
              } else if (key === 'status') {
                this.chips.push({ name: Array.isArray(value) ? value[0] : value, type: key });
              }
            }
            this.careerFilterForm.get(key)?.setValue(null);
          } else {
            this.careerFilterForm.get(key)?.setValue(value);
          }
        });
      }
    }
  }

  private getMasterData() {
    this.loadJobCategories();
    this.loadJobSkills();
    this.getCountries(null);
    this.changeLocations();
  }

  private changeLocations() {
    this.careerFilterForm.get('countries')?.valueChanges.subscribe((data: any) => {
      if (data && typeof data === 'string') {
        this.getCountries({ startsWith: data });
      }
    });

    this.careerFilterForm.get('states')?.valueChanges.subscribe((data: any) => {
      if (data && typeof data === 'string') {
        const countryChips = this.chips.filter(chip => chip.type === 'countries');
        if (countryChips.length > 0) {
          const lastCountry = countryChips[countryChips.length - 1].name;
          this.getStatesByCountryName({ countryName: lastCountry, startsWith: data });
        } else {
          this.getStatesByCountryName({ startsWith: data });
        }
      }
    });

    this.careerFilterForm.get('cities')?.valueChanges.subscribe((data: any) => {
      if (data && typeof data === 'string') {
        const stateChips = this.chips.filter(chip => chip.type === 'states');
        if (stateChips.length > 0) {
          const lastState = stateChips[stateChips.length - 1].name;
          this.getCityiesByStateName({ stateName: lastState, startsWith: data });
        } else {
          this.getCityiesByStateName({ startsWith: data });
        }
      }
    });
  }

  private loadJobCategories() {
    let pageNumber = 0;
    let pageSize = 100;
    let allJobCategories: any[] = [];
    let morePages = true;

    this.masterDataService.getJobCategories(pageNumber, pageSize, '')
      .pipe(
        takeWhile(() => morePages),
        concatMap(response => {
          if (response.data.length === 0) {
            morePages = false;
            return [];
          }
          allJobCategories = allJobCategories.concat(response.data);
          pageNumber++;
          return this.masterDataService.getJobCategories(pageNumber, pageSize, '');
        })
      )
      .subscribe(data => {
        allJobCategories = allJobCategories.concat(data.data);
        this.jobCategories = allJobCategories;
        this.filteredCategories = this.jobCategories;
      });
  }

  private loadJobSkills() {
    let pageNumber = 0;
    let pageSize = 100;
    let allJobSkills: any[] = [];
    let morePages = true;

    this.masterDataService.getJobSkills(pageNumber, pageSize, '')
      .pipe(
        takeWhile(() => morePages),
        concatMap(response => {
          if (response.data.length === 0) {
            morePages = false;
            return [];
          }
          allJobSkills = allJobSkills.concat(response.data);
          pageNumber++;
          return this.masterDataService.getJobSkills(pageNumber, pageSize, '');
        })
      )
      .subscribe(data => {
        allJobSkills = allJobSkills.concat(data.data);
        this.jobSkills = allJobSkills;
        this.filteredJobSkills = this.jobSkills;
      });
  }

  onPostedDateSelected(event: any) {
    if (event.value) {
      const formattedDate = this.formatDate(event.value);
      this.addChip(formattedDate, 'postedDate');
      this.careerFilterForm.get('postedDate')?.setValue(null);
      this.postedDatePicker.close();
    }
  }

  filterCareer() {
    const jobPostFilter: any = {};
    const arrayFields = ['jobCategories', 'skills', 'countries', 'states', 'cities'];
    const singleValueFields = ['status', 'experience', 'company'];
    const groupedChips = this.chips.reduce((acc: any, curr) => {
      if (!acc[curr.type]) {
        acc[curr.type] = [];
      }
      if (arrayFields.includes(curr.type)) {
        acc[curr.type].push(curr.name);
      } else if (curr.type === 'experience') {
        acc[curr.type] = parseInt(curr.name.replace(' years', ''), 10);
      } else if (curr.type === 'postedDate') {
        const dateParts = curr.name.split(' ');
        const month = new Date(Date.parse(`${dateParts[0]} 1, 2000`)).getMonth();
        const day = parseInt(dateParts[1].replace(',', ''), 10);
        const year = parseInt(dateParts[2], 10);
        const isoDate = new Date(year, month, day, 18, 30, 0).toISOString();
        acc[curr.type] = isoDate;
      } else if (singleValueFields.includes(curr.type)) {
        acc[curr.type] = curr.name;
      }
      return acc;
    }, {});

    Object.keys(groupedChips).forEach(key => {
      jobPostFilter[key] = groupedChips[key];
    });

    jobPostFilter.showOnlyActiveJobPosts = this.careerFilterForm.get('showOnlyActiveJobPosts')?.value || false;

    const isFilterEmpty = Object.keys(jobPostFilter).length === 0 ||
                         (Object.keys(jobPostFilter).length === 1 &&
                          'showOnlyActiveJobPosts' in jobPostFilter &&
                          !jobPostFilter.showOnlyActiveJobPosts);

    if (isFilterEmpty) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Filters',
        text: 'Please select at least one filter!',
        confirmButtonText: 'OK',
      });
      return;
    }

    this.dialogRef.close(JSON.parse(JSON.stringify(jobPostFilter)));
    this.careerFilterForm.reset();
    this.chips = [];
  }

  formatDate(dateStr: any): string {
    const date = new Date(dateStr);
    return `${date.toLocaleString('default', { month: 'long' })} ${`0${date.getDate()}`.slice(-2)}, ${date.getFullYear()}`;
  }

  private checkAndConvertArray(value: any): any[] {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
  }

  cancelCareerFilter() {
    this.careerFilterForm.reset();
    this.chips = [];
    this.dialogRef.close(null);
  }

  clearCareerFilter() {
    this.careerFilterForm.reset();
    this.chips = [];
    this.clearCareersFilter.emit(this.careerFilterForm.value);
  }

  getCountries(params: any) {
    this.executeGetRequestURL(MweConstants.countryURL, params, 'countryDropdown');
  }

  getStatesByCountryName(params: any) {
    this.executeGetRequestURL(MweConstants.stateUrl, params, 'states');
  }

  getCityiesByStateName(params: any) {
    this.executeGetRequestURL(MweConstants.cityUrl, params, 'cities');
  }

  executeGetRequestURL(url: string, params: any, type: string) {
    (this as any)[type] = [];
    this.masterDataService.executeGetRequestURL(url, params).subscribe({
      next: (data: any) => {
        if (data && data?.data && data.data.length > 0) {
          (this as any)[type] = data.data;
        }
      },
      error: (error: any) => {
        console.error(`Error fetching ${type}:`, error);
      }
    });
  }

  onKeyPress(event: KeyboardEvent, fieldName: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = (event.target as HTMLInputElement).value;

      if (value && value.trim().length > 0) {
        if (fieldName === 'company') {
          this.addChip(value, 'company');
          this.careerFilterForm.get('company')?.setValue(null);
        } else if (fieldName === 'experience') {
          this.addChip(`${value} years`, 'experience');
          this.careerFilterForm.get('experience')?.setValue(null);
        }
      }
    }
  }

  onCountrySelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'countries')) {
      this.careerFilterForm.get('countries')?.setValue('');
    }
  }

  onStateSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'states')) {
      this.careerFilterForm.get('states')?.setValue('');
    }
  }

  onCitySelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'cities')) {
      this.careerFilterForm.get('cities')?.setValue('');
    }
  }

  onJobCategorySelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'jobCategories')) {
      this.careerFilterForm.get('jobCategories')?.setValue('');
    }
  }

  onSkillSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'skills')) {
      this.careerFilterForm.get('skills')?.setValue('');
    }
  }

  onStatusSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (this.addChip(value, 'status')) {
      this.careerFilterForm.get('status')?.setValue(null);
    }
  }

  onExperienceChange(event: any) {
    const value = event.target.value;
    if (value) {
      this.addChip(`${value} years`, 'experience');
      this.careerFilterForm.get('experience')?.setValue(null);
    }
  }

  onCompanyChange(event: any) {
    const value = event.target.value;
    if (value) {
      this.addChip(value, 'company');
      this.careerFilterForm.get('company')?.setValue(null);
    }
  }

  addChip(name: string, type: string): boolean {
    if (!name || name.trim() === '') {
      return false;
    }
    const singleValueFields = ['status', 'postedDate', 'experience', 'company'];
    if (singleValueFields.includes(type)) {
      this.chips = this.chips.filter(chip => chip.type !== type);
    }
    if (this.chips.some(chip => chip.name === name && chip.type === type)) {
      return false;
    }

    this.chips.push({ name, type });
    return true;
  }

  remove(chip: { name: string, type: string }) {
    this.chips = this.chips.filter(c => !(c.name === chip.name && c.type === chip.type));
    const arrayFields = ['jobCategories', 'skills', 'countries', 'states', 'cities'];
    const singleValueFields = ['status', 'postedDate', 'experience', 'company'];
    if (arrayFields.includes(chip.type)) {
      this.careerFilterForm.get(chip.type)?.setValue('');
    } else if (singleValueFields.includes(chip.type)) {
      this.careerFilterForm.get(chip.type)?.setValue(null);
    }
  }

  onCityFocus() {
    const stateChips = this.chips.filter(chip => chip.type === 'states');
    if (stateChips.length > 0) {
      this.getCityiesByStateName({ stateName: stateChips[stateChips.length - 1].name });
    } else if (!this.cities || this.cities.length === 0) {
      this.getCityiesByStateName({});
    }
  }

  onStateFocus() {
    const countryChips = this.chips.filter(chip => chip.type === 'countries');
    if (countryChips.length > 0) {
      this.getStatesByCountryName({ countryName: countryChips[countryChips.length - 1].name });
    } else if (!this.states || this.states.length === 0) {
      this.getStatesByCountryName({});
    }
  }

  filteredCategories: any[] = [];
  filterJobCategories(event: any) {
    const searchTerm = (event.target?.value || '').toLowerCase().trim();
    this.filteredCategories = this.jobCategories.filter((item: any) =>
      item.categoryName.toLowerCase().includes(searchTerm)
    );
  }

  filteredJobSkills: any[] = [];
  filterJobSkills(event: any) {
    const searchTerm = (event.target?.value || '').toLowerCase().trim();
    this.filteredJobSkills = this.jobSkills.filter((item: any) =>
      item.name.toLowerCase().includes(searchTerm)
    );
  }

  onStatusFocus() {
    this.filteredStatusOptions = this.statusOptions;
  }

  filterStatus(event: any) {
    const filterValue = (event.target?.value || '').toLowerCase();
    this.filteredStatusOptions = this.statusOptions.filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  ngAfterViewInit() {
    const triggers = [
      { trigger: this.autoCompleteCountryInput, control: 'countries' },
      { trigger: this.autoCompleteStateInput, control: 'states' },
      { trigger: this.autoCompleteCityInput, control: 'cities' },
      { trigger: this.autoCompleteStatusInput, control: 'status' },
      { trigger: this.autoCompleteJobCategoryInput, control: 'jobCategories' },
      { trigger: this.autoCompleteSkillInput, control: 'skills' }
    ];

    triggers.forEach(({ trigger, control }) => {
      if (trigger) {
        trigger.panelClosingActions.subscribe(e => {
          if (!e || !e.source) {
            this.careerFilterForm.get(control)?.setValue('');
          }
        });
      }
    });
  }
}
